import { type VcsApi } from '@env0/vcs-service/api';
import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import useApiClient from 'hooks/use-api-client';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import { useCurrentProjectId } from 'hooks/path-params-extraction.hooks';
import { useHasPermission } from 'hooks/use-has-permission';

export const useGetVcsConnections = () => {
  const { isAuthorized: canViewVcsConnections } = useHasPermission(['VIEW_VCS_CONNECTIONS', 'MANAGE_VCS_CONNECTIONS'], {
    permissionOperator: 'OR'
  });
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'vcs', 'connections'],
    queryFn: () => apiClient.vcsConnections.listVcsConnections(orgId!),
    enabled: canViewVcsConnections && Boolean(orgId)
  });
};

export const useFindVcsConnection = (
  enabled: boolean,
  vcsType: VcsApi.VcsType,
  connectionType: VcsApi.ConnectionType,
  repositoryUrl?: string
) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'vcs', 'connections', vcsType, connectionType, repositoryUrl],
    queryFn: () => apiClient.vcsConnections.findVcsConnection(orgId!, vcsType, connectionType, repositoryUrl!),
    enabled: Boolean(repositoryUrl) && enabled
  });
};

type GetVcsRepositoriesOptions = Pick<VcsApi.GetVcsRepositories.Request.QueryParams, 'search' | 'page' | 'perPage'>;

const SEARCH_RESULTS_STALE_TIME_MS = 5 * 60 * 1000;

export const useGetVcsRepositories = (
  vcsType: string,
  connectionType: VcsApi.ConnectionType,
  // When omitted, the full list is fetched (legacy behaviour, used by callers that need every repo).
  // The repository dropdowns pass paging/search so opening them no longer enumerates the whole installation.
  options?: GetVcsRepositoriesOptions,
  enabled = true
): UseQueryResult<VcsApi.GetVcsRepositories.Response> => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'vcs', 'repositories', vcsType, connectionType, options],
    queryFn: () => apiClient.vcsConnections.getVcsRepositories(orgId!, vcsType, connectionType, options),
    enabled: !!vcsType && enabled,
    // Legacy full-list fetches are cached forever; search/paged results are cached briefly so
    // clearing/retyping a query reuses the result instead of re-hitting the (slow) agent path.
    staleTime: options ? SEARCH_RESULTS_STALE_TIME_MS : Infinity,
    // Keep showing the previous page while a new search is in flight (isFetching still signals loading),
    // instead of emptying the dropdown for the whole round trip.
    placeholderData: options ? keepPreviousData : undefined
  });
};

// The repository the dropdown was just picked from is already in some loaded page or search result, so read every
// cached repositories response for this vcsType/connectionType before asking the server again.
export const useCachedVcsRepository = (
  vcsType: string,
  connectionType: VcsApi.ConnectionType,
  httpsUrl: string | undefined
): VcsApi.Repository | undefined => {
  const orgId = useCurrentOrganizationId();
  const queryClient = useQueryClient();

  if (!httpsUrl) return undefined;

  return queryClient
    .getQueriesData<VcsApi.GetVcsRepositories.Response>({
      queryKey: [orgId, 'vcs', 'repositories', vcsType, connectionType]
    })
    .flatMap(([, data]) => data?.repositories ?? [])
    .find(repository => repository.httpsUrl === httpsUrl);
};

export const useGetVcsRepositoriesById = (vcsConnectionId: string) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'vcs', 'repositories', vcsConnectionId],
    queryFn: () => apiClient.vcsConnections.getVcsRepositoriesById(orgId!, vcsConnectionId),
    enabled: !!vcsConnectionId
  });
};

export const useCreateVcsConnection = () => {
  const orgId = useCurrentOrganizationId();
  const projectId = useCurrentProjectId();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vcsConnection: Omit<VcsApi.CreateVcsConnection.Request.Body, 'organizationId'>) =>
      apiClient.vcsConnections.createVcsConnection({ ...vcsConnection, organizationId: orgId, projectId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [orgId, 'vcs'] });
    }
  });
};

export const useUpdateVcsConnection = () => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: VcsApi.UpdateVcsConnection.Request.Body }) =>
      apiClient.vcsConnections.updateVcsConnection(id, data),
    onSuccess: () => () => queryClient.invalidateQueries({ queryKey: [orgId, 'vcs'] })
  });
};

export const useDeleteVcsConnection = () => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => apiClient.vcsConnections.deleteVcsConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [orgId, 'vcs'] });
    }
  });
};

export const useGetVcsConnectionDependents = (vcsConnectionId: string | undefined, enabled: boolean) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'vcs', 'dependents', vcsConnectionId],
    queryFn: () => apiClient.vcsConnections.getVcsConnectionDependents(vcsConnectionId!),
    enabled: enabled && !!vcsConnectionId
  });
};

export const useGetVcsConnectionHealth = (vcsConnectionId: string) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'vcs', 'health', vcsConnectionId],
    queryFn: () => apiClient.vcsConnections.getVcsConnectionHealth(vcsConnectionId),
    enabled: !!vcsConnectionId
  });
};
